import React from 'react';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import Button from '@/components/Button/newButtons';
import { dashboardSetSelectedList } from '@/modules/dashboard/dashboardActions';
import { UserRole } from '@/modules/user/userTypes';
import { useSequencesContext } from '../../SequencesContext';
import AiSequenceDialogForm from '../AiModal/molecules/AiSequenceDialogForm';

export default function SequencesContainerHeader() {
	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserFreeTier =
		user?.role === UserRole.Free || user?.role === UserRole.PreTrial;

	const {
		setSequencePage,
		setSelectedSequence,
		setSelectedTemplate,
		setAiSequenceModalFields,
		templates,
		showAiSequenceModal,
		setShowAiSequenceModal
	} = useSequencesContext();

	const dispatch = useDispatch();

	const handleNewSequence = () => {
		dispatch(dashboardSetSelectedList(null));

		setSelectedSequence({
			name: 'New sequence',
			steps: [],
			status: 'DRAFT'
		});
		if (templates) {
			setSelectedTemplate(undefined);
		}
		setSequencePage('SEQUENCE-START');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'user_clicked_new_sequence'
		});
	};

	const resetPreviousAiGeneratedSequence = () => {
		setAiSequenceModalFields(undefined);
		setSelectedTemplate(undefined);
	};

	return (
		<div className="mb-2 mr-2 flex flex-shrink-0 flex-col gap-3 rounded-lg border border-dm-grey bg-dm-black p-4 lg:h-20 lg:flex-row lg:items-center lg:pb-[10px] lg:pt-1">
			<div className="flex flex-shrink items-center justify-between gap-x-4 gap-y-1 lg:flex-shrink-0 lg:flex-col lg:items-start lg:justify-start">
				<div className="h-8 text-sm lg:w-[9.625rem]">
					{isUserFreeTier ? (
						<AiSequenceDialogForm
							isOpen={showAiSequenceModal}
							setIsOpen={setShowAiSequenceModal}
							trigger={
								<Button
									rightIcon={<PlusSignIcon />}
									onClick={resetPreviousAiGeneratedSequence}
								>
									New sequence
								</Button>
							}
						/>
					) : (
						<Button
							rightIcon={<PlusSignIcon />}
							onClick={handleNewSequence}
						>
							New sequence
						</Button>
					)}
				</div>
			</div>
			<div className="h-8 w-full">
				<InputWithIcon />
			</div>
		</div>
	);
}

function InputWithIcon() {
	const { setSequenceSearch, sequenceSearch } = useSequencesContext();

	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		setSequenceSearch(e.target.value);
	}

	return (
		<div className="relative h-full w-full">
			<input
				type="text"
				placeholder="Search a Sequence"
				className="h-full w-full text-sm lg:text-lg"
				value={sequenceSearch}
				onChange={handleChange}
			/>
			<button
				type="submit"
				className="absolute bottom-0 right-2 top-0 flex items-center text-orange"
			>
				<SearchIconNew />
			</button>
		</div>
	);
}

export function SearchIconNew() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			className="h-6 w-6"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
			/>
		</svg>
	);
}
function PlusSignIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
			className="h-5 w-5"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M12 4.5v15m7.5-7.5h-15"
			/>
		</svg>
	);
}
